import { useEffect, useState } from 'react';
import { useAchievements, useAckCelebration, useCelebrations, useUpdateShowcase } from '../../api/hooks';
import { setCelebrationPending } from '../../celebrationGate';
import { accentOf, Medallion } from './Medallion';
import { haptics } from '../../telegram';
import { track } from '../../analytics';
import type { Celebration } from '../../api/types';

/**
 * «Момент получения» (C1): bottom-sheet поверх приложения, когда у игрока есть
 * непоказанные уровни. Несколько — пейджер точками; «Забрать» ack'ает текущий.
 * Варианты (design_handoff_mastery / qualification 2): M05 — легендарный лист
 * с «Закрепить в витрине»; sel_* — лист отбора (латунь), sel_champ+sel_finalist
 * склеиваются в один лист («+ Финалист» чипом, ack обоих).
 * Монтируется в App.tsx; сервер каппит очередь (10) и не выдаёт ретро-начисления
 * (suppressCelebrations при recalc-all).
 */
export function CelebrationSheet({ enabled }: { enabled: boolean }) {
  const celebrations = useCelebrations(enabled);
  const ack = useAckCelebration();
  const summary = useAchievements(null);
  const updateShowcase = useUpdateShowcase();
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [index, setIndex] = useState(0);

  const queue = (celebrations.data ?? []).filter((c) => !dismissed.includes(key(c)));
  // SA5: финиш отбора — champ и finalist одним листом (champ главный).
  const champ = queue.find((c) => c.code === 'sel_champ');
  const mergedFinalist = champ ? queue.find((c) => c.code === 'sel_finalist') ?? null : null;
  const items = mergedFinalist ? queue.filter((c) => c !== mergedFinalist) : queue;

  const visible = enabled && items.length > 0;
  // Рекап отбора ждёт, пока очередь достижений не опустеет (celebrationGate).
  useEffect(() => {
    setCelebrationPending('achievements', visible);
    return () => setCelebrationPending('achievements', false);
  }, [visible]);
  if (!visible) return null;
  const current = items[Math.min(index, items.length - 1)];

  const code = current.code ?? '';
  const legendary = code === 'M05';
  const sel = code.startsWith('sel_');
  const withFinalist = current === champ && mergedFinalist !== null;
  const finish = code === 'sel_champ' || code === 'sel_finalist';
  const label = legendary
    ? 'Легендарное достижение'
    : !sel
      ? 'Новое достижение'
      : finish
        ? 'Итоги отбора'
        : current.maxLevel > 1
          ? `Новая ступень · ${current.level} из ${current.maxLevel}`
          : 'Достижение отбора';

  const take = () => {
    haptics.success();
    track('celebration_ack', { id: current.achievementId, level: current.level });
    void ack.mutateAsync({ achievementId: current.achievementId, level: current.level }).catch(() => undefined);
    if (withFinalist && mergedFinalist) {
      void ack
        .mutateAsync({ achievementId: mergedFinalist.achievementId, level: mergedFinalist.level })
        .catch(() => undefined);
      setDismissed((d) => [...d, key(current), key(mergedFinalist)]);
    } else {
      setDismissed((d) => [...d, key(current)]);
    }
    setIndex(0);
  };

  // MA3: «Закрепить в витрине» на легендарном листе (если есть слот).
  const showcase = summary.data?.showcase ?? [];
  const canPin =
    legendary && !showcase.some((s) => s.id === current.achievementId) && showcase.length < 3;
  const pinAndTake = async () => {
    haptics.tap();
    try {
      await updateShowcase.mutateAsync([...showcase.map((s) => s.id), current.achievementId]);
      track('showcase_pin', { id: current.achievementId, from: 'celebration' });
    } catch {
      /* нет слота/гонка — просто забираем */
    }
    take();
  };

  const sheetCls = [
    'sheet',
    'ach-celebrate',
    legendary ? 'ach-celebrate--legendary' : '',
    sel ? 'ach-celebrate--sel' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="sheet-backdrop ach-celebrate-backdrop">
      <div className={sheetCls} data-theme="dark" onClick={(e) => e.stopPropagation()}>
        <div className="ach-celebrate-label">{label}</div>
        {/* key: переключение по точкам пересоздаёт блок → pop и искры
            переигрываются для каждого достижения, не только для первого. */}
        <div
          className="ach-celebrate-medallion"
          key={`${current.achievementId}:${current.level}`}
        >
          <span className="sparks" aria-hidden>
            <i /><i /><i /><i /><i /><i />
          </span>
          <Medallion icon={current.icon} level={current.level} maxLevel={current.maxLevel}
            progress={1} size={legendary ? 108 : sel ? 104 : 84} glow
            variant={accentOf(code, current.level, current.maxLevel)} />
        </div>
        <div className="ach-celebrate-name">{current.name}</div>
        <div className="ach-celebrate-level">
          {current.levelTitle ??
            (current.maxLevel > 1 ? `Уровень ${current.level}` : 'Получено')}{' '}
          · +{current.points + (withFinalist ? mergedFinalist!.points : 0)} очков
        </div>
        {withFinalist && (
          <div className="ach-celebrate-extra">
            <span className="ach-chip ach-chip--qual">+ Финалист</span>
          </div>
        )}
        {items.length > 1 && (
          <div className="ach-celebrate-dots">
            {items.map((c, i) => (
              <button
                key={key(c)}
                className={i === Math.min(index, items.length - 1) ? 'on' : ''}
                onClick={() => setIndex(i)}
                aria-label={`Достижение ${i + 1}`}
              />
            ))}
          </div>
        )}
        <button className="btn ach-celebrate-take" onClick={take}>
          {legendary ? 'Отлично' : 'Забрать'}
        </button>
        {canPin && (
          <button
            className="ach-celebrate-pin"
            onClick={() => void pinAndTake()}
            disabled={updateShowcase.isPending}
          >
            Закрепить в витрине
          </button>
        )}
      </div>
    </div>
  );
}

function key(c: Celebration): string {
  return `${c.achievementId}:${c.level}`;
}
